import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { sendVerificationCode, loginWithGoogleCredential } from "../services/auth.service";
import Layout from "../PageLayout/LoginLayout";
import { GoogleLogin } from "@react-oauth/google";

export default function RegisterForm() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const continueBtnRef = useRef(null);
  const [googleWidth, setGoogleWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      try {
        const w = continueBtnRef.current?.offsetWidth || 0;
        if (w && Math.abs(w - googleWidth) > 2) setGoogleWidth(w);
      } catch {}
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [googleWidth]);

  const handleContinue = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setError("");
    if (!email) {
      setError("Please enter your email");
      return;
    }

    try {
      setSubmitting(true);
      await sendVerificationCode(email);
      try {
        localStorage.setItem("pendingVerificationEmail", email);
      } catch (_) {}
      navigate(`/verify-email?email=${encodeURIComponent(email)}`, { state: { email } });
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || err?.message || "Failed to send verification code.";
      console.error("Error sending email:", err);
      if (status === 409 || /exists/i.test(msg)) {
        alert("Email already exists");
        setError("Email already exists");
      } else {
        setError(msg);
      }
    }
    finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div
        className="flex flex-col justify-start">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" transform="scale(-1,1) translate(-20,0)" />
          </svg>
          Back
        </button>
        <h2 className="text-2xl font-semibold mb-1">Register</h2>
        <p className="text-sm text-gray-500 mb-6">
          You have an account?{" "}
          <Link
            to="/login"
            className="text-indigo-600 font-medium hover:underline"
          >
            Login
          </Link>
        </p>

        <form onSubmit={handleContinue} className="w-full max-w-sm">
          {/* Email Input */}
          <div className="mb-4 w-full">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name.surname@company.com"
              className="w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Continue Button */}
          <button
            type="submit"
            disabled={submitting}
            ref={continueBtnRef}
            className="w-full bg-[#1E1E2F] text-white rounded-md py-2 text-sm font-medium hover:bg-gray-800 mb-2 disabled:opacity-60"
          >
            {submitting ? "Sending..." : "Continue"}
          </button>
        </form>
        {error && <div className="w-full max-w-sm mb-4 text-sm text-red-600">{error}</div>}

        {/* Divider */}
        <div className="flex items-center max-w-sm w-full mb-4">
          <hr className="flex-grow border-gray-200" />
          <span className="mx-3 text-xs text-gray-400">or</span>
          <hr className="flex-grow border-gray-200" />
        </div>

        {/* Google Login - match width and behavior as Login page */}
        <div className="w-full max-w-sm">
          <GoogleLogin
            width={googleWidth ? String(googleWidth) : undefined}
            logo_alignment="center"
            onSuccess={async (credentialResponse) => {
              try {
                setError("");
                const cred = credentialResponse?.credential;
                if (!cred) throw new Error("No Google credential received");
                await loginWithGoogleCredential(cred);
                navigate("/overview");
              } catch (err) {
                setError(err?.response?.data?.message || err?.message || "Google sign-in failed");
              }
            }}
            onError={() => setError("Google sign-in was cancelled or failed")}
            useOneTap={false}
            shape="pill"
            theme="outline"
            size="large"
            text="continue_with"
          />
        </div>
      </div>
    </Layout>
  );
}
